<template>
  <q-popover ref="popover" class="time-tracking__customer__popover" @show="handleShow" @hide="handleHide">
    <div class="q-pa-md">
      <q-search
        v-model="searchQuery"
        inverted-light
        color="blue-grey-1"
        class="time-tracking__customer__search"
        :debounce="defaultSearchDebounce"
        @input="handleSearch"
      />

      <div class="time-tracking__customer__list q-mt-md">
        <template v-show="hasCustomers">
          <q-infinite-scroll
            ref="infiniteScroll"
            inline
            style="height: 200px; overflow: auto;"
            :handler="handleLoadMore"
          >
            <div class="q-mb-xs">
              <div
                class="time-tracking__customer hover-highlight q-py-xs q-px-sm row items-center cursor-pointer"
                @click="handleSelectCustomer(null)"
              >
                <q-icon name="business" class="invisible"></q-icon>
                <span class="q-subheading text-italic text-faded on-right">{{ $t('without_customer') }}</span>
              </div>
            </div>

            <div
              v-for="customer in customers"
              :key="customer.id"
              class="q-mb-xs"
            >
              <customer-indicator
                clickable
                :customer="customer"
                :active="isActiveCustomer(customer)"
                @click="handleSelectCustomer(customer)"
              />
            </div>
          </q-infinite-scroll>
        </template>

        <template v-if="!hasCustomers">
          <div class="fit row justify-center items-center">
            <span class="text-faded">{{ $t('no_customers') }}</span>
          </div>
        </template>
      </div>
    </div>
  </q-popover>
</template>

<script>
  import CustomerIndicator from './CustomerIndicator.vue'
  import { CustomersClient } from 'src/api/Customers'
  import debounceMixin from 'src/utils/mixins/debounce'

  export default {
    inject: ['$validator'],

    props: {
      activeCustomer: {
        type: Object,
        default: null
      }
    },

    data () {
      return {
        customers: [],
        customersMeta: null,
        searchQuery: null
      }
    },

    computed: {
      hasCustomers () {
        return !!this.customers.length
      },

      isSearch () {
        return !!this.searchQuery
      },

      reachedCustomersEnd () {
        return this.customersMeta ? this.currentPage === this.customersMeta.pagination.total_pages : false
      },

      currentPage () {
        return this.customersMeta ? this.customersMeta.pagination.current_page : 0
      }
    },

    methods: {
      handleSearch () {
        if (!this.isSearch) {
          return this.fetchCustomersList(1, true)
            .then(this.resetList)
        }

        this.fetchCustomerSearchResults(this.searchQuery, 1, true)
          .then(this.resetList)
      },

      handleSelectCustomer (customer) {
        this.$emit('select-customer', customer)

        this.$refs.popover.hide()
      },

      handleShow () {
        this.fetchCustomersList(1, true)
      },

      handleHide () {
        this.reset()
      },

      handleLoadMore (index, done) {
        if (this.reachedCustomersEnd) {
          return done(true)
        }

        const nextPage = this.currentPage + 1

        const fetch = this.isSearch ? this.fetchCustomerSearchResults(this.searchQuery, nextPage) : this.fetchCustomersList(nextPage)

        fetch.then(done)
      },

      fetchCustomersList (page, reset = false) {
        return new CustomersClient()
          .sort('name:asc')
          .page(page)
          .list()
          .then(response => {
            const customers = reset ? [] : this.customers

            this.customers = customers.concat(response.data.data)
            this.customersMeta = response.data.meta
          })
      },

      fetchCustomerSearchResults (query, page, reset = false) {
        return new CustomersClient()
          .page(page)
          .search(query)
          .then(response => {
            const customers = reset ? [] : this.customers

            this.customers = customers.concat(response.data.data)
            this.customersMeta = response.data.meta
          })
      },

      isActiveCustomer (customer) {
        return this.activeCustomer && this.activeCustomer.id === customer.id
      },

      resetList () {
        if (!this.$refs.infiniteScroll) {
          return
        }

        this.$refs.infiniteScroll.$el.scrollTop = 0

        this.$refs.infiniteScroll.reset()
        this.$refs.infiniteScroll.resume()
      },

      reset () {
        this.searchQuery = null
        this.resetList()
      }
    },

    components: {
      CustomerIndicator
    },

    mixins: [
      debounceMixin
    ]
  }
</script>

<style lang="stylus">
  @import '~variables'

  .time-tracking
    &__customer
      &__popover
        width: 270px
      &__search
        box-shadow: none
        border-radius: 20px
      &__list
        height: 200px
        overflow-y: scroll
</style>
